// Tashkilotlar reyestri — `xsib-identity` `tenant` moduli bilan kontrakt.
//
//   GET  /api/tenants               → TenantView[]   (HAMMASI, to'xtatilgani ham)
//   GET  /api/tenants/{id}          → TenantView
//   POST /api/tenants               → TenantProvisionResult   (201)
//   PUT  /api/tenants/{id}          → TenantView
//   PUT  /api/tenants/{id}/state    → TenantView
//
// Huquqlar: o'qish `tenant.read`, yozish `tenant.create` / `tenant.update`.
// Ustiga backend GLOBAL ko'rinish doirasini ham talab qiladi — ya'ni
// amalda faqat SUPERADMIN. Panel buni `useIsPlatformOwner` bilan
// takrorlaydi (qulaylik, xavfsizlik emas).
//
// ⚠️ ENDPOINT `xsib-identity` DA, `xsib-platform` da EMAS — garchi
// `platform.tenant` jadvali platform'niki bo'lsa ham. Sabab: yangi
// tashkilot "ishga tayyor" bo'lishi uchun tashkilot qatori BILAN BIRGA
// rollar, huquqlar va birinchi admin ham bitta tranzaksiyada yaratilishi
// kerak, va ularning uchtasi identity'niki. Batafsil —
// `TenantRepository` (identity) javadoc'i.
//
// ⚠️ O'CHIRISH ENDPOINTI YO'Q va bu ataylab. Tashkilotda abonentlar,
// o'zgarmas moliyaviy jurnal (`ledger`) va audit bor; `DELETE` esa
// `ON DELETE CASCADE` bo'ylab identity yozuvlarini olib ketardi. To'g'ri
// amal — `state = SUSPENDED`.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'
import type { TableState } from '@/shared/ui/table/tableState'

export const TENANT_STATES = ['ACTIVE', 'SUSPENDED'] as const
export type TenantState = (typeof TENANT_STATES)[number]

export const BUSINESS_KINDS = ['ISP', 'VENUE'] as const
export type BusinessKind = (typeof BUSINESS_KINDS)[number]

const tenantSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  businessKind: z.enum(BUSINESS_KINDS),
  currency: z.string(),
  dayTimezone: z.string(),
  locale: z.string(),
  /**
   * ⚠️ `null` bo'lishi MUMKIN va bu NORMAL holat.
   *
   * Realm 2026-08-06 dan ixtiyoriy (`V202608062800`): bitta tashkilotli
   * o'rnatmada u kerak emas va abonent RADIUS'ga TOZA login bilan
   * boradi (`firdavs`, `firdavs@af-isp.xsib` emas).
   *
   * Bu yerda `z.string()` qolgani BUTUN tashkilotlar sahifasini
   * yiqitardi: zod javobni parslay olmasdi va jadval umuman
   * chizilmasdi — sabab esa "realm null" bo'lardi, ya'ni butunlay
   * boshqa joyda izlanardi.
   */
  realm: z.string().nullish(),
  state: z.enum(TENANT_STATES),
  memberCount: z.number(),
  createdAt: z.string().nullish(),
})

/**
 * ⚠️ `adminPassword` — BIR MARTALIK. Server uni faqat shu javobda
 * qaytaradi va hech qayerda saqlamaydi. Panel uni HAM saqlamaydi:
 * modal yopilishi bilan qiymat yo'qoladi (`useState`), `localStorage`
 * ga yozilmaydi va `queryClient` keshiga tushmaydi (mutatsiya natijasi
 * query kesh emas).
 *
 * `null` bo'ladi, agar parolni operatorning o'zi kiritgan bo'lsa.
 */
const provisionResultSchema = z.object({
  tenant: tenantSchema,
  adminUserId: uuidSchema,
  adminUsername: z.string(),
  adminPassword: z.string().nullish(),
  rolesCreated: z.number(),
})

export type TenantEntry = z.infer<typeof tenantSchema>
export type TenantProvisionResult = z.infer<typeof provisionResultSchema>

export interface TenantDraft {
  code: string
  name: string
  businessKind: BusinessKind
  currency: string
  dayTimezone?: string
  locale?: string
  adminUsername: string
  adminDisplayName?: string
  /** Bo'sh bo'lsa server kuchli parol yaratadi va javobda bir marta qaytaradi. */
  adminPassword?: string
}

export interface TenantUpdate {
  name: string
  dayTimezone?: string
  locale?: string
}

/** URL filtri kaliti */
export const TENANT_STATE_FILTER = 'state'

/** «Barcha holatlar» — filtrning tanlovi, tashkilot holati EMAS */
export const TENANT_STATE_ALL = 'ALL'

/**
 * Sukut ko'rinish — FAQAT AMALDAGI tashkilotlar (egasi qarori 2026-08-20).
 *
 * ⚠️ To'xtatilganni QAYTA YOQISH yo'li YO'QOLMAYDI: holat filtrida
 * «To'xtatilgan» tanlovi turadi va u aynan shu ro'yxatni beradi. Boshqa
 * yo'l yo'q — `TenantsPage` da o'chirish tugmasi ham, alohida arxiv ham
 * mavjud emas.
 *
 * ⚠️ Filtr MIJOZDA ishlaydi va bu YOLG'ON EMAS: `GET /api/tenants`
 * birorta parametr qabul qilmaydi va BARCHA tashkilotni bitta ro'yxatda
 * beradi (fayl sarlavhasi), sahifalash ham yo'q. Ya'ni filtr to'liq
 * ma'lumot ustidan yuradi.
 */
export const TENANT_DEFAULT_STATE: TenantState = 'ACTIVE'

/**
 * Ekrandagi va amaldagi holat tanlovi.
 *
 * ⚠️ ATAYLAB AJRATILGAN funksiya: sukut qiymatni CHAQIRIB o'lchash
 * kerak (`tests/closed-default.mjs`). Noma'lum qiymat sukutga qaytadi —
 * buzuq havola to'xtatilganlarni jimgina qaytarib olib kelmasin.
 */
export function tenantStateFilter(state: TableState): string {
  const selected = state.filters[TENANT_STATE_FILTER]?.[0]
  if (selected === TENANT_STATE_ALL) return TENANT_STATE_ALL
  if (selected !== undefined && (TENANT_STATES as readonly string[]).includes(selected)) {
    return selected
  }
  return TENANT_DEFAULT_STATE
}

export async function fetchTenants(signal?: AbortSignal): Promise<TenantEntry[]> {
  const response = await api.get<unknown>('/api/tenants', { signal })
  return z.array(tenantSchema).parse(response.data)
}

export async function createTenant(draft: TenantDraft): Promise<TenantProvisionResult> {
  const response = await api.post<unknown>('/api/tenants', draft)
  return provisionResultSchema.parse(response.data)
}

export async function updateTenant(id: string, update: TenantUpdate): Promise<TenantEntry> {
  const response = await api.put<unknown>(`/api/tenants/${id}`, update)
  return tenantSchema.parse(response.data)
}

export async function setTenantState(
  id: string,
  state: TenantState,
  reason?: string,
): Promise<TenantEntry> {
  const response = await api.put<unknown>(`/api/tenants/${id}/state`, { state, reason })
  return tenantSchema.parse(response.data)
}

// --- Mijoz tomonidagi tekshiruv -------------------------------------
//
// Server baribir qayta tekshiradi (`TenantCodeRules`), lekin xato
// SERVERGA BORMASDAN ko'rinsa operator uni yozayotgan paytda tuzatadi.
// Qoidalar server bilan AYNAN bir xil bo'lishi shart — ular bir joyda
// o'zgarsa ikkinchisi ham o'zgaradi.

/** `realm` qo'shimchasi — `platform.fill_tenant_realm()` bilan bir xil. */
export const REALM_SUFFIX = '.xsib'

/** `tenant_realm_valid` dagi `length(realm) <= 63` dan kelib chiqadi. */
export const MAX_CODE_LENGTH = 63 - REALM_SUFFIX.length

const CODE_SHAPE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

/** FreeRADIUS `proxy.conf` da band nomlar — `tenant_realm_not_reserved`. */
const RESERVED = new Set(['local', 'default', 'null'])

export type TenantCodeError = 'required' | 'tooLong' | 'shape' | 'reserved' | null

export function validateTenantCode(code: string): TenantCodeError {
  const trimmed = code.trim()
  if (trimmed === '') return 'required'
  if (trimmed.length > MAX_CODE_LENGTH) return 'tooLong'
  if (!CODE_SHAPE.test(trimmed)) return 'shape'
  if (RESERVED.has(trimmed)) return 'reserved'
  return null
}

/** Operator qanday realm chiqishini YOZAYOTGANDA ko'rsin. */
export function realmPreview(code: string): string {
  return `${code.trim().toLowerCase()}${REALM_SUFFIX}`
}
